/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subscription } from 'rxjs';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, mergeMap, skip, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { LessonInterface } from '../../../models/interface/lesson.interface';
import { PayloadInterface } from '../../../models/interface/payload.interface';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { InterventionActionTypes } from '../../../store/intervention/intervention.action';
import { InterventionInstanceActionTypes } from '../../../store/intervention-instance/intervention-instance.action';
import { PatientSharedService } from '../../../services/shared/patient-shared/patient-shared.service';
import { UserActionTypes } from '../../../store/user/user.action';
import { UserStore } from '../../../store/user/component-store/user.store';
import { InterventionInstanceReminderInterface } from '../../../models/interface/intervention-instance-reminder.interface';
import { ReminderActionTypes } from '../../../store/reminder/reminder.action';
import { StudyActionTypes } from '../../../store/study/study.action';
import { StudyStore } from '../../../store/study/component-store/study.store';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';
import { StudyService } from 'src/app/services/api/study/study.service';

/**
 * Component:
 * Patient instances page displaying all assigned interventions to the patient;
 * Can be found: {uri}/patients/{{user_id}}/instances
 */
@Component({
  selector: 'app-patient-instance',
  templateUrl: './patient-instance.component.html',
  styleUrls: ['./patient-instance.component.scss'],
  providers: [UserStore, StudyStore],
})
export class PatientInstanceComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Filter
  public filter = { searchFilter: '', excludeCanceledInstance: true };

  // Study ID and loading status for this child component
  patientId: number;
  public isLoadingSubject = new BehaviorSubject<boolean>(true);
  public isLoading$ = this.isLoadingSubject.asObservable();

  // Data provided by UserService
  public users: UserInterface[] = [];
  public collaborators: UserInterface[] = [];

  public studies: StudyInterface[] = [];

  public interventions: InterventionInterface[] = [];

  // Checkbox
  public isCollab: boolean;
  public isCollabSubject = new BehaviorSubject<boolean>(true);

  public pagedInstances$: Observable<InterventionInstanceInterface[]>;

  // Confirm instance payload
  public payload: PayloadInterface = null;

  // Translation for study name
  public param = { intervention_name: '...' };

  public instances$: Observable<InterventionInstanceInterface[]>;

  public allQuestionnaires$: Observable<LessonInterface[]>;

  public isManager$: Observable<boolean>;

  public profile$: Observable<ProfileInterface>;

  public allInterventionInstanceReminders$: Observable<InterventionInstanceReminderInterface[]>;

  private collabInterventions$: Observable<InterventionInterface[]>;

  private pagedInstancesSubject = new BehaviorSubject<InterventionInstanceInterface[]>([]);

  private searchTextSubject = new BehaviorSubject<string>('');

  private myInstances: InterventionInstanceInterface[] = [];

  private allInstancesForECoach$: Observable<InterventionInstanceInterface[]>;

  private myMembers$: Observable<UserInterface[]>;

  private instancesSubject = new BehaviorSubject<InterventionInstanceInterface[]>([]);

  private subscriptions: Subscription[] = [];

  constructor(
    private sharedService: PatientSharedService,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private translateService: TranslateService,
    private studyService: StudyService,
    private store: Store<{
      myProfile: ProfileInterface;
      collabInterventions: InterventionInterface[];
      allInstancesForECoach: InterventionInstanceInterface[];
      allQuestionnaires: LessonInterface[];
      getCollaboratorsByStudyId: { studyId: number; collaborators: UserInterface[] };
      myMembers: UserInterface[];
      allInterventionInstanceReminders: InterventionInstanceReminderInterface[];
    }>,
  ) {
    this.instances$ = this.instancesSubject.asObservable();
    this.pagedInstances$ = this.pagedInstancesSubject.asObservable();
    this.profile$ = store.select('myProfile');
    this.collabInterventions$ = store.select('collabInterventions');
    this.allInstancesForECoach$ = store.select('allInstancesForECoach');
    this.allInterventionInstanceReminders$ = this.store.select('allInterventionInstanceReminders');
    this.allQuestionnaires$ = this.store.select('allQuestionnaires');
    this.myMembers$ = this.store.select('myMembers');
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    this.store.dispatch({
      type: StudyActionTypes.getCollaboratingStudiesType,
      payload: { include: 'owners,roles' },
    });

    this.store.dispatch({
      type: ReminderActionTypes.getAllInterventionInstanceRemindersOfUserType,
      payload: {},
    });

    this.subscriptions.push(
      this.isCollabSubject.subscribe((value) => {
        this.isCollab = value;
      }),
    );

    this.subscriptions.push(
      this.sharedService.sourceId$
        .pipe(
          filter((value) => value != null),
          mergeMap((value) => {
            this.patientId = value;
            this.store.dispatch({
              type: InterventionActionTypes.getInterventionsOfCollabStudyType,
              payload: {},
            });
            return this.collabInterventions$.pipe(skip(1), take(1));
          })
        )
        .subscribe((result) => {
          this.interventions = result;
          this.applyInstanceFilter();
        }),
    );

    this.subscriptions.push(
      this.translateService.onLangChange.subscribe((_event: LangChangeEvent) => {
        this.applyInstanceFilter();
      }),
    );

    this.sharedService.pushActiveChild('instances');
  }

  public applyInstanceFilter(): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadInstanceList(true).subscribe(() => {
        this.isLoadingSubject.next(false);
      }),
    );
  }

  public reloadInstanceList(setFirst?: boolean): Observable<boolean> {
    const pageIndex = this.paginator?.paginator ? this.paginator?.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;
    this.store.dispatch({
      type: ReminderActionTypes.getAllInterventionInstanceRemindersOfUserType,
      payload: {},
    });
    this.store.dispatch({
      type: InterventionInstanceActionTypes.getAllInstancesForECoachType,
      payload: { patientId: this.patientId },
    });

    return this.allInstancesForECoach$.pipe(
      mergeMap((result: InterventionInstanceInterface[]) => {
        this.myInstances = result;
        if (this.filter.excludeCanceledInstance) {
          this.myInstances = this.myInstances.filter(
            (instance) => instance.attributes.progress.current_state !== 'canceled',
          );
        }
        const studyIDs: number[] = [];
        for (const instance of this.myInstances) {
          const studyId = instance.attributes.study_id;
          if (!studyIDs.includes(studyId)) {
            studyIDs.push(studyId);
          }
        }
        const studies = this.studyService.getAllStudies({
          include: 'collaborators',
          studyIDs: studyIDs,
        });
        this.store.dispatch({
          type: UserActionTypes.getMyMembersType,
          payload: {},
        });
        const myMembers = this.myMembers$.pipe(skip(1), take(1));
        return forkJoin({ studies, myMembers });
      }),
      mergeMap((results) => {
        const studyCheck = this.helperService.getSafe(() => results.studies.body.data, undefined);
        this.studies = studyCheck ?? [];
        const userCheck = this.helperService.getSafe(() => results.myMembers, undefined);
        this.users = userCheck ?? [];
        return this.search(this.filter.searchFilter).pipe(
          mergeMap(
            () => this.helper.setPagedContent(
              this.instancesSubject,
              this.pagedInstancesSubject,
              setFirst,
              pageIndex,
              pageSize,
            ),
          ),
        );
      })
    );
  }

  public updateList(): void {
    this.subscriptions.push(this.reloadInstanceList().subscribe());
  }

  public updatePagedInterventionInstances(event: InterventionInstanceInterface[]) {
    if (event) {
      this.pagedInstancesSubject.next(event);
    }
  }

  public getStudyById(studyId: number) {
    return this.studies.find((study) => study.id.toString() === studyId.toString());
  }

  // Reads search value and runs searchFilter
  private search(name: string): Observable<boolean> {
    this.searchTextSubject.next(name);
    return this.searchTextSubject.pipe(
      distinctUntilChanged(),
      mergeMap(() => {
        this.instancesSubject.next(this.filterWithInput(this.filter.searchFilter.toLowerCase().trim()));
        return of(true);
      }),
    );
  }

  // Filter for instance_id and instance_state
  private filterWithInput(value: string): InterventionInstanceInterface[] {
    return this.myInstances.filter((instance) => {
      if (instance.id !== null) {
        const ltInstanceId = instance.id.toString().trim().toLowerCase();
        if (ltInstanceId.includes(value)) {
          return true;
        }
      }
      if (instance.attributes.progress.current_state) {
        const stateSubject = new BehaviorSubject<string>('');
        this.translateService
          .get('group-instance.state_' + instance.attributes.progress.current_state.toLowerCase())
          .subscribe((translation: string) => {
            stateSubject.next(translation);
          });
        const ltInstanceState = stateSubject.value.trim().toLowerCase();
        if (ltInstanceState.includes(value)) {
          return true;
        }
      }
      return false;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
